import { useMemo } from 'react';
import { Axis } from '@visx/xychart';

import type { YAxisProps } from '../../types/YAxisProps';
import { createYAxisFormatterFromTicks, getYAxisFormatter } from '../../utils/formatters';
import { useChartContext } from '../../utils/useChartContext';

/**
 * YAxis component for composable LineChart.
 *
 * Thin wrapper around visx's Axis component with LaunchPad-specific defaults.
 * Supports automatic formatting via `dataType` prop.
 *
 * Uses domain-aware formatting when tick values are available - this ensures
 * consistent scaling across all y-axis ticks (e.g., all use K or all use plain numbers,
 * never mixing like "800" and "1.5k").
 *
 * @example
 * ```tsx
 * // Automatic formatting based on data type
 * <LineChart>
 *   <YAxis dataType="money" numTicks={5} />
 *   <Series ... />
 * </LineChart>
 *
 * // Custom formatting (takes precedence over dataType)
 * <LineChart>
 *   <YAxis tickFormat={(v) => `${v}ms`} numTicks={5} />
 *   <Series ... />
 * </LineChart>
 * ```
 */
export function YAxis({
  orientation = 'left',
  dataType,
  tickFormat,
  numTicks = 5,
  label,
  labelOffset,
  fallbackMin: _fallbackMin,
  fallbackMax: _fallbackMax,
  ...rest
}: YAxisProps) {
  const { yTickValues } = useChartContext();

  // Resolve tick formatter: explicit tickFormat takes precedence over dataType
  // When we have tick values and a dataType, use domain-aware formatting to ensure
  // consistent scaling (e.g., all ticks use K, or all use plain numbers)
  const resolvedTickFormat = useMemo(() => {
    if (tickFormat) {
      return tickFormat;
    }
    // Use domain-aware formatter when we have tick values
    if (yTickValues && yTickValues.length > 0 && dataType) {
      return createYAxisFormatterFromTicks(yTickValues, dataType);
    }
    return getYAxisFormatter(dataType);
  }, [tickFormat, dataType, yTickValues]);

  return (
    <Axis
      orientation={orientation}
      numTicks={numTicks}
      tickValues={yTickValues} // Use explicit tick values if provided
      label={label}
      tickFormat={resolvedTickFormat}
      labelOffset={labelOffset ?? (label ? 55 : 0)}
      hideAxisLine
      hideTicks
      {...rest}
    />
  );
}

// displayName for identifying YAxis in children extraction (supports React.memo)
YAxis.displayName = 'YAxis';
